import { ReactElement, useState } from "react"

import { Button, Modal } from "@wfp/ui"
import { iconAddOutline } from "@wfp/icons"

import { faClipboardList, faRotate, faTag } from "@fortawesome/free-solid-svg-icons"
import { faHourglassHalf } from "@fortawesome/free-regular-svg-icons"

import { useAppSelector } from "../../hooks/hooksStore"
import { useGetReportQuery } from "../../services/marketprices/reports"
import { selectUser } from "../../features/common/commonSlice"
import { checkAccess } from "../../utils/helpers"

import { MARKET_MODULE } from "../../constants/constants"
import { ReportStatuses } from "../../constants/prices"
import { ContentType } from "../../constants/markets"

import ReportCard from "../../components/Markets/Dashboard/ReportCard"
import NewDataContent from "../../components/Markets/Dashboard/NewDataContent"
import PriceUploads from "../../components/Markets/Dashboard/PriceUploads"
import FetchOperations from "../../components/Markets/Dashboard/FetchOperations"
import DashboardCard from "../../components/Markets/Dashboard/DashboardCard"
import FetchData from "../../components/Markets/Dashboard/FetchData"

const DashboardPage = (): ReactElement => {
  const [openModal, setOpenModal] = useState<boolean>(false)
  const user = useAppSelector(selectUser)

  const checkReview = checkAccess(user?.roles, [MARKET_MODULE], "reports", "review")

  const { data: progressReports, isFetching: isLoadingReports } = useGetReportQuery({
    skip: 0,
    take: 5,
    adm0Code: 0,
    status: ReportStatuses.InProgress,
  })
  const { data: pendingReports, isFetching: isLoadingPendingReports } = useGetReportQuery({
    skip: 0,
    take: 5,
    adm0Code: 0,
    status: ReportStatuses.Pending,
  })

  return (
    <div className="flex justify-center w-full px-2 flex-col gap-6">
      <div className="flex gap-4 justify-between w-full items-center">
        <div>
          <p className="text-xl font-bold">Market Price Monitoring Dashboard</p>
          <p className="text-sm text-gray-500">Overview of data uploads, tasks and synchronization status. </p>{" "}
          <p className="text-sm text-gray-500 !mt-0.5">
            {new Date().toLocaleDateString("en-GB", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
        <Button icon={iconAddOutline} iconReverse onClick={() => setOpenModal(true)}>
          Add Data
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ReportCard
          data={progressReports}
          title="In-progress reports"
          subtitle="Select a report to finalize submission"
          isEmpty={progressReports?.count === 0}
          isLoading={isLoadingReports}
          status={ReportStatuses.InProgress}
          icon={faClipboardList}
        />
        <ReportCard
          data={pendingReports}
          isEmpty={pendingReports?.count === 0}
          title="Pending reports"
          subtitle="Select a report to validate and approve"
          isLoading={isLoadingPendingReports}
          status={ReportStatuses.Pending}
          havePermission={checkReview}
          icon={faHourglassHalf}
        />
      </div>

      <div className="mt-6 w-full">
        <DashboardCard
          title="Price Data Upload Status"
          icon={faTag}
          subtitle="See the number of observations uploaded by status in the last six months"
        >
          <PriceUploads />
        </DashboardCard>
      </div>

      <div className="mt-6 w-full">
        <DashboardCard title="MODA Data Fetch Status" icon={faRotate}>
          <FetchOperations />
        </DashboardCard>
      </div>

      <div className="mt-6 w-full">
        <DashboardCard title="MODA Data Fetch Errors" icon={faRotate}>
          <FetchData />
        </DashboardCard>
      </div>

      <Modal
        open={openModal}
        onRequestClose={() => setOpenModal(false)}
        modalLabel="Add Price Data: Choose how to add new market price data"
        passiveModal
        lazyLoad
      >
        <NewDataContent moda={ContentType.moda} excel={ContentType.excel} />
      </Modal>
    </div>
  )
}

export default DashboardPage
